import Immutable from 'immutable';

import type {
    AsyncOperationMessage,
    AsyncOperationNameEnum,
    TaskResultMessage,
    WebSocketMessage,
} from 'js/src/client';
import {
    AsyncOperationMessageTypeEnum,
    TaskResultMessageTypeEnum,
    Urls,
    WebSocketCloseCode,
    WebSocketKeepAliveMessage,
} from 'js/src/client';
import { ImmutableFromJS, type ImmutableMap, isNotUndefined } from 'js/src/libs/consts';
import { createAbortController } from 'js/src/libs/createAbortController';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import PollAsyncOperation from 'js/src/libs/poll_async_operation';
import type { PartialResultCallback, PatchedTaskResultResponse } from 'js/src/libs/poll_async_task';
import PollAsyncTask, { IS_PARTIAL_RESPONSE } from 'js/src/libs/poll_async_task';
import type { ErrorObject } from 'js/src/types';
import {
    isAsyncOperationName,
    isErrorObject,
    MaxRequestsError,
    OperationAbortedError,
} from 'js/src/types';
import Cookies from 'js-cookie';
import { WebSocket as ReconnectingWebSocket } from 'partysocket';
import type { ErrorEvent } from 'partysocket/ws';

/* eslint-disable prefer-promise-reject-errors */

type CloseEventReason = {
    code: number;
    reason: string;
    wasClean: boolean;
};
type CloseEvent = {
    code: number | string;
    reason: string | CloseEventReason;
    wasClean: boolean;
};

/**
 * CloseEvent does not always match what the MDN spec says. This parses out the code and reason
 * based on shape seen in Sentry events
 */
function parseCloseEvent(event: CloseEvent) {
    let code = typeof event.code === 'number' ? event.code : 0;
    let reason = typeof event.reason === 'string' ? event.reason : '';
    if (
        typeof event.code !== 'number' &&
        typeof event.reason === 'object' &&
        typeof event.reason.code === 'number'
    ) {
        code = event.reason.code;
        reason = event.code || event.reason.reason;
    }
    return {
        code,
        reason,
        wasClean: event.wasClean,
    };
}

function isTaskResultMessageHandler(
    messageHandler: MessageHandler,
): messageHandler is TaskResultMessageHandler {
    return messageHandler.type === TaskResultMessageTypeEnum.TASK_RESULT;
}

export const WS_ERROR_MESSAGES = {
    INVALID_TASK_NAME: 'Invalid task name',
    MESSAGE_PARSE_ERROR: 'Error parsing WebSocket message',
    INVALID_MESSAGE_FORMAT: 'Invalid WebSocket message format',
    TIMEOUT: 'Timed out waiting for message',
};

enum STOP_POLLING_REASON {
    CONNECTION_OPEN = 'CONNECTION_OPEN',
    MESSAGE_HANDLED = 'MESSAGE_HANDLED',
}

function isStopPollingReason(reason: string): reason is STOP_POLLING_REASON {
    return Object.values(STOP_POLLING_REASON).includes(reason as STOP_POLLING_REASON);
}

type PartialResponseHandler<T = PatchedTaskResultResponse | AsyncOperationMessage['result']> =
    PartialResultCallback<T>;
type Handlers = {
    handleSuccess: (messageData: unknown) => void;
    handleError: (error: string | ErrorObject) => void;
    handlePartialResponse: PartialResponseHandler;
};
type AsyncOperationMessageHandler = {
    uid: string;
    type: AsyncOperationMessageTypeEnum;
    taskName: AsyncOperationNameEnum;
    timeout: ReturnType<typeof setTimeout>;
    handlers: Handlers[];
};
type TaskResultMessageHandler = {
    uid: string;
    type: TaskResultMessageTypeEnum;
    taskName: string;
    timeout: ReturnType<typeof setTimeout>;
    handlers: Handlers[];
};
type MessageHandler = AsyncOperationMessageHandler | TaskResultMessageHandler;

type Options = {
    url?: string;
    startClosed?: boolean;
    _newInstance?: boolean; // To allow multiple instances for tests. Do not use in production
    _CustomRWS?: typeof ReconnectingWebSocket; // For tests to mock the ReconnectingWebSocket
};

export const PING_INTERVAL_SECONDS = 30000; // Same as PING_TIMEOUT_SECONDS in websocket_views.py

export class WebSocketClient {
    MAX_RETRIES: number = 5;
    EXPECTED_PING_TIMEOUT: number = PING_INTERVAL_SECONDS + 1000; // Expected time to receive PING from the server
    TIMEOUT: number = 600000; // 10 minutes
    MAX_RESULTS: number = 100;

    static #instance: WebSocketClient;
    #options: Options = {};
    #socket: ReconnectingWebSocket | null = null;

    #expectPingTimeout: ReturnType<typeof setTimeout> | null = null;

    #messageHandlers: Record<string, MessageHandler> = {};
    #results: Immutable.OrderedMap<string, ImmutableMap<WebSocketMessage>> = Immutable.OrderedMap();
    // Store active polls for each message. The abort controller is used to cancel the polling when polling is no longer needed
    #activePolls: Record<string, AbortController> = {};

    constructor(opts: Options = {}) {
        if (!opts._newInstance && WebSocketClient.#instance) {
            // eslint-disable-next-line no-console
            console.warn('WebSocketClient already instantiated');
            return WebSocketClient.#instance;
        }
        WebSocketClient.#instance = this;
        this.#options = opts;

        this._handleOpen = this._handleOpen.bind(this);
        this._handleClose = this._handleClose.bind(this);
        this._handleMessage = this._handleMessage.bind(this);
        this._handleError = this._handleError.bind(this);

        this.connect();
    }

    static getInstance() {
        if (!WebSocketClient.#instance) {
            // eslint-disable-next-line no-console
            console.warn('WebSocketClient not instantiated. Please instantiate on page load.');
            ErrorReporter.capture(
                `WebSocketClient not instantiated on page ${window.location.pathname}. Please instantiate on page load.`,
                { level: 'warning' },
            );
            new WebSocketClient();
        }
        return WebSocketClient.#instance;
    }

    #getUrl() {
        if (this.#options?.url) {
            return this.#options.url;
        }
        return Urls.WEBSOCKET_CONNECT;
    }

    /**
     * Clean up any handlers for a message that has been processed
     * @param uid message unique identifier
     */
    #cleanupMessageHandlers(uid: string) {
        if (this.#messageHandlers[uid]) {
            clearTimeout(this.#messageHandlers[uid].timeout);
            delete this.#messageHandlers[uid];
        }
        if (this.#activePolls[uid]) {
            this.#activePolls[uid].abort(STOP_POLLING_REASON.MESSAGE_HANDLED);
            delete this.#activePolls[uid];
        }
    }

    #pollAllAsyncTasks() {
        const messageHandlers = Object.values(this.#messageHandlers).filter(
            isTaskResultMessageHandler,
        );
        if (messageHandlers.length === 0) {
            return;
        }

        const taskArgs = messageHandlers
            .map(({ uid, taskName, handlers }) => {
                const handlePartialResponse: PartialResponseHandler = (
                    partialResult,
                    isPartialResponse,
                ) => {
                    handlers.forEach((h) => {
                        h.handlePartialResponse(partialResult, isPartialResponse);
                    });
                };
                if (this.#activePolls[uid]) {
                    return;
                }
                const abortController = createAbortController();
                this.#activePolls[uid] = abortController;
                return {
                    task_id: uid,
                    task_name: taskName,
                    abort_controller: abortController,
                    partial_response_callback: handlePartialResponse,
                };
            })
            .filter(isNotUndefined);
        if (taskArgs.length === 0) {
            return;
        }
        const promises = PollAsyncTask.bulk_poll_for_results(taskArgs);
        promises.forEach(async (promise, i) => {
            const taskId = taskArgs[i].task_id;
            const messageHandler = this.#messageHandlers[taskId];
            try {
                const result = await promise;
                messageHandler.handlers.forEach(({ handleSuccess }) => {
                    handleSuccess(result);
                });
                return result;
            } catch (e: unknown) {
                let error: ErrorObject;
                if (isErrorObject(e)) {
                    if (
                        e.error instanceof OperationAbortedError &&
                        isStopPollingReason(e.error.reason)
                    ) {
                        return e;
                    }
                    error = e;
                } else {
                    error = {
                        error: Error(`${messageHandler.taskName}: Unknown error`, { cause: e }),
                    };
                }
                messageHandler.handlers.forEach(({ handleError }) => {
                    handleError(error);
                });
                return error;
            }
        });
    }

    async #pollForResult(uid: string, abortController: AbortController | null, pollOnce = false) {
        const messageHandler = this.#messageHandlers[uid];
        const handlePartialResponse: PartialResponseHandler = (
            partialResult,
            isPartialResponse,
        ) => {
            messageHandler.handlers.forEach((h) => {
                h.handlePartialResponse(partialResult, isPartialResponse);
            });
        };
        try {
            let result = null;
            if (messageHandler.type === AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT) {
                result = await PollAsyncOperation.poll_for_result(
                    uid,
                    messageHandler.taskName,
                    abortController,
                    pollOnce,
                );
            } else {
                result = await PollAsyncTask.poll_for_result<PatchedTaskResultResponse>(
                    uid,
                    messageHandler.taskName,
                    handlePartialResponse,
                    abortController,
                    pollOnce,
                );
            }
            messageHandler.handlers.forEach(({ handleSuccess }) => {
                handleSuccess(result);
            });
            return result;
        } catch (e: unknown) {
            let error: ErrorObject;
            if (isErrorObject(e)) {
                if (
                    e.error instanceof OperationAbortedError &&
                    isStopPollingReason(e.error.reason)
                ) {
                    return e;
                }
                if (pollOnce && e.error instanceof MaxRequestsError) {
                    return e;
                }
                error = e;
            } else {
                error = {
                    error: Error(`${messageHandler.taskName}: Unknown error`, { cause: e }),
                };
            }
            messageHandler.handlers.forEach(({ handleError }) => {
                handleError(error);
            });
            return error;
        }
    }

    /**
     * Start polling for any outstanding messages that have not been received yet.
     * A single poll will be started for each message so this can be called multiple times
     */
    #startFallbackToPolling() {
        Object.values(this.#messageHandlers).forEach((messageHandler) => {
            if (isTaskResultMessageHandler(messageHandler)) {
                // task results are polled in bulk
                return;
            }
            const uid = messageHandler.uid;
            if (this.#activePolls[uid]) {
                // Already polling
                return;
            }
            const abortController = createAbortController();
            this.#activePolls[uid] = abortController;
            this.#pollForResult(uid, abortController);
        });

        this.#pollAllAsyncTasks();
    }

    #stopPolling() {
        Object.values(this.#activePolls).forEach((abortController) => {
            abortController.abort(STOP_POLLING_REASON.CONNECTION_OPEN);
        });
        this.#activePolls = {};
    }

    #startPingTimeout() {
        this.#expectPingTimeout = setTimeout(() => {
            // If a ping is not received within the expected time then connection with server
            // has been lost, attempt to reconnect
            // It's possible the client did not receive a close event so also trigger fallback to polling
            this.#socket?.reconnect();
            this.#startFallbackToPolling();
        }, this.EXPECTED_PING_TIMEOUT);
    }

    #messageDataToJSON(data: unknown) {
        if (typeof data !== 'string') {
            throw Error(WS_ERROR_MESSAGES.MESSAGE_PARSE_ERROR);
        }

        let parsedData;
        try {
            parsedData = JSON.parse(data);
        } catch (e) {
            throw Error(WS_ERROR_MESSAGES.MESSAGE_PARSE_ERROR);
        }

        if (!parsedData.type || !parsedData.uid) {
            throw Error(WS_ERROR_MESSAGES.INVALID_MESSAGE_FORMAT);
        }

        switch (parsedData.type) {
            case AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT:
                return parsedData as AsyncOperationMessage;
            case TaskResultMessageTypeEnum.TASK_RESULT:
                return parsedData as TaskResultMessage;
            default:
                throw Error(WS_ERROR_MESSAGES.INVALID_MESSAGE_FORMAT);
        }
    }

    _handleOpen(event: Event) {
        ErrorReporter.addBreadcrumb({
            category: 'websocket',
            message: 'WebSocket connection opened',
        });
        this.#stopPolling();
        this.#startPingTimeout();

        // Poll one more time in case the message was received while disconnected
        Object.keys(this.#messageHandlers).forEach((uid) => {
            const pollOnce = true;
            this.#pollForResult(uid, null, pollOnce);
        });
    }

    _handleClose(event: CloseEvent) {
        const { code, reason, wasClean } = parseCloseEvent(event);
        const retry = this.#socket?.retryCount || 0;
        ErrorReporter.addBreadcrumb({
            category: 'websocket',
            message: 'WebSocket connection closed',
            data: {
                code,
                reason,
                wasClean,
                retryCount: this.#socket?.retryCount || 0,
            },
        });
        if (code === WebSocketCloseCode.WINDOW_CLOSED) {
            return;
        }

        this.#startFallbackToPolling();

        if (this.#expectPingTimeout) {
            clearTimeout(this.#expectPingTimeout);
            this.#expectPingTimeout = null;
        }

        if (code === WebSocketCloseCode.NORMAL) {
            // Normal closure, do not record
            return;
        }
        if (retry >= this.MAX_RETRIES) {
            ErrorReporter.capture(Error('WebSocket connection failed'));
        }
    }

    _handleError(event: ErrorEvent) {
        ErrorReporter.addBreadcrumb({
            category: 'websocket',
            message: 'WebSocket error',
            data: {
                error: event.error,
                readyState: this.#socket?.readyState,
            },
        });
    }

    _handleMessage(event: MessageEvent) {
        const data = event.data;

        // Plain test messages
        if (data === WebSocketKeepAliveMessage.PING) {
            this.#expectPingTimeout && clearTimeout(this.#expectPingTimeout);
            this.#startPingTimeout();
            return;
        }

        // JSON messages
        let parsedData: WebSocketMessage;
        try {
            parsedData = this.#messageDataToJSON(data);
        } catch (e: unknown) {
            ErrorReporter.capture(e, { extra: data });
            return;
        }
        ErrorReporter.addBreadcrumb({
            category: 'websocket',
            message: 'WebSocket message received',
            data: {
                ...parsedData,
                // Don't send full payload because it can be quite large
                result: !!parsedData.result,
                partial_result: !!(parsedData as TaskResultMessage).partial_result,
            },
        });

        // Partial results send multiple messages for the same task
        // If its received after the final result, do not store it
        const resultReceived = !!this.#results.getIn([parsedData.uid, 'result']);
        if (!resultReceived) {
            this.#results = this.#results.set(parsedData.uid, ImmutableFromJS(parsedData));
        }

        const messageHandler: MessageHandler | undefined = this.#messageHandlers[parsedData.uid];
        if (messageHandler) {
            messageHandler.handlers.forEach((h) => {
                if (parsedData.error) {
                    h.handleError(parsedData.error);
                } else if (parsedData.result) {
                    h.handleSuccess(parsedData.result);
                } else if (
                    parsedData.type === TaskResultMessageTypeEnum.TASK_RESULT &&
                    messageHandler.type === TaskResultMessageTypeEnum.TASK_RESULT &&
                    parsedData.partial_result !== null
                ) {
                    h.handlePartialResponse(parsedData.partial_result, IS_PARTIAL_RESPONSE);
                } else {
                    ErrorReporter.capture(WS_ERROR_MESSAGES.INVALID_MESSAGE_FORMAT, {
                        extra: parsedData,
                    });
                }
            });
        }

        while (this.#results.size > this.MAX_RESULTS) {
            const first = this.#results.first();
            if (first) {
                this.#results = this.#results.delete(first.get('uid'));
            }
        }
    }

    connect() {
        if (
            this.#socket &&
            (this.#socket.readyState === WebSocket.OPEN ||
                this.#socket.readyState === WebSocket.CONNECTING)
        ) {
            // eslint-disable-next-line no-console
            console.warn('WebSocket already connected');
            return;
        }
        const url = this.#getUrl();
        // Smuggle the CSRF token as protocol header
        const protocol = Cookies.get('csrftoken') || null;
        const WS = this.#options._CustomRWS || ReconnectingWebSocket;
        this.#socket = new WS(url, protocol, {
            maxRetries: this.MAX_RETRIES,
            startClosed: this.#options.startClosed || false,
        });

        this.#socket.onopen = this._handleOpen;
        this.#socket.onclose = this._handleClose;
        this.#socket.onmessage = this._handleMessage;
        this.#socket.onerror = this._handleError;

        // close on window close
        window.addEventListener('beforeunload', () => {
            this.close(WebSocketCloseCode.WINDOW_CLOSED);
        });
    }

    /**
     * Wait for a message from a WebSocket
     * A message must be send by the server with the same type
     * and unique message ID (e.g. task ID or the Async Operation UUID)
     *
     * @param uid Message unique identifier
     * @param type Used to determine message data shape and which endpoint to poll as fallback
     * @param taskName Used for logging and polling
     * @param options.abortController Optional AbortController to cancel the message
     * @param options.partialResponseCallback Optional callback to handle partial responses
     */
    async waitForMessage<ResponseType>(
        uid: string,
        type:
            | AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT
            | TaskResultMessageTypeEnum.TASK_RESULT,
        taskName: string | AsyncOperationNameEnum,
        {
            abortController,
            partialResponseCallback,
        }: {
            abortController?: AbortController;
            partialResponseCallback?: PartialResponseHandler<ResponseType>;
        } = {},
    ): Promise<ResponseType> {
        const waitForStart = Date.now();
        const origin = new Error();
        ErrorReporter.addBreadcrumb({
            category: 'websocket',
            message: 'WebSocket waitForMessage started',
            data: {
                uid,
                type,
                taskName,
            },
        });
        return new Promise((resolve, reject) => {
            const handleSuccess: Handlers['handleSuccess'] = (messageData: unknown) => {
                this.#cleanupMessageHandlers(uid);
                resolve(messageData as ResponseType);
            };
            const handlePartialResponse: Handlers['handlePartialResponse'] = (
                messageData: unknown,
                isPartialResponse?: boolean,
            ) => {
                if (!partialResponseCallback) {
                    return;
                }
                partialResponseCallback(messageData as ResponseType, isPartialResponse);
            };
            const handleError: Handlers['handleError'] = (error: ErrorObject | string) => {
                this.#cleanupMessageHandlers(uid);
                let rejectError = null;
                if (typeof error === 'string') {
                    rejectError = { error: new Error(error, { cause: origin }) };
                } else {
                    error.error.cause = origin;
                    rejectError = error;
                }
                reject(rejectError);
            };
            const timeout = setTimeout(() => {
                const timeoutStart = Date.now();
                const pollOnce = true;
                this.#pollForResult(uid, null, pollOnce).then((retval) => {
                    if (isErrorObject(retval) && retval.error instanceof MaxRequestsError) {
                        this.#messageHandlers[uid].handlers.forEach(({ handleError }) => {
                            handleError(`${taskName}: ${WS_ERROR_MESSAGES.TIMEOUT}`);
                        });
                        return;
                    }
                    // During OS sleep, websocket is disconnected and timers are suspended
                    // On wakeup, the callback runs late and a successful result is not an error outcome
                    const likelySleep = timeoutStart - waitForStart >= this.TIMEOUT + 1000;
                    if (
                        !likelySleep &&
                        // Check one more time for result in case it was received after final poll
                        !this.#results.get(uid) &&
                        this.#socket?.readyState === WebSocket.OPEN
                    ) {
                        ErrorReporter.capture(
                            'WebSocket timed out but result was retrieved via polling',
                            {
                                extra: {
                                    uid,
                                    type,
                                    taskName,
                                },
                            },
                        );
                    }
                });
            }, this.TIMEOUT);

            if (type === AsyncOperationMessageTypeEnum.ASYNC_OPERATION_RESULT) {
                if (!isAsyncOperationName(taskName)) {
                    handleError(`${taskName}: ${WS_ERROR_MESSAGES.INVALID_TASK_NAME}`);
                    return;
                }
                this.__messageHandlers = {
                    ...this.#messageHandlers,
                    [uid]: {
                        uid,
                        type,
                        taskName: taskName,
                        timeout,
                        handlers: [
                            ...(this.#messageHandlers[uid]?.handlers || []),
                            {
                                handleSuccess,
                                handleError,
                                handlePartialResponse,
                            },
                        ],
                    },
                };
            } else {
                this.__messageHandlers = {
                    ...this.#messageHandlers,
                    [uid]: {
                        uid,
                        type,
                        taskName,
                        timeout,
                        handlers: [
                            ...(this.#messageHandlers[uid]?.handlers || []),
                            {
                                handleSuccess,
                                handleError,
                                handlePartialResponse,
                            },
                        ],
                    },
                };
            }

            if (abortController) {
                // Caller no longer needs to wait for the message
                if (abortController.signal.aborted) {
                    handleError({
                        error: new OperationAbortedError(
                            `${taskName}: ${abortController.signal.reason}`,
                        ),
                    });
                    return;
                }
                abortController.signal.addEventListener(
                    'abort',
                    () => {
                        handleError({
                            error: new OperationAbortedError(
                                `${taskName}: ${abortController.signal.reason}`,
                            ),
                        });
                    },
                    { once: true },
                );
            }

            const messageResult = this.#results.get(uid)?.toJS();
            if (messageResult) {
                // Message was already recieved
                if (messageResult.error) {
                    handleError(messageResult.error);
                } else if (messageResult.result) {
                    handleSuccess(messageResult.result as ResponseType);
                } else if (
                    messageResult.type === TaskResultMessageTypeEnum.TASK_RESULT &&
                    messageResult.partial_result
                ) {
                    handlePartialResponse(messageResult.partial_result, IS_PARTIAL_RESPONSE);
                }
                return;
            }

            if (this.#socket?.readyState !== WebSocket.OPEN) {
                this.#startFallbackToPolling();
            }
        });
    }

    close(code: number = WebSocketCloseCode.NORMAL) {
        // Server does not support closing with a reason
        // Use a code only otherwise the code code is not echoed back in the CloseEvent
        this.#socket && this.#socket.close(code);
    }

    // @internal
    // Cleanup the client state, used for tests to ensure a clean state
    __cleanup() {
        this.close(WebSocketCloseCode.WINDOW_CLOSED);
        this.#expectPingTimeout && clearTimeout(this.#expectPingTimeout);
        Object.keys(this.#messageHandlers).forEach((uid) => {
            this.#cleanupMessageHandlers(uid);
        });
        this.#results = this.#results.clear();
    }

    // @internal
    get __messageHandlers() {
        return this.#messageHandlers;
    }

    // Setter is used to allow tests to mock
    set __messageHandlers(value) {
        this.#messageHandlers = value;
    }

    // @internal
    get __results() {
        return this.#results;
    }

    // @internal
    get __activePolls() {
        return this.#activePolls;
    }

    // @internal
    get __socket() {
        return this.#socket;
    }

    // @internal
    get __expectPingTimeout() {
        return this.#expectPingTimeout;
    }
}

export default WebSocketClient;
